import { createContext, useContext, useState } from "react";

const AppointmentContext = createContext(undefined);

export const useAppointment = () => {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error(
      "useAppointment must be used within an AppointmentProvider",
    );
  }
  return context;
};

export const AppointmentProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [patientInfo, setPatientInfo] = useState({
    fullName: "",
    fatherName: "",
    contact: "",
    cnic: "",
    address: "",
    occupation: "",
    sourceInfo: "",
    gender: "",
    age: null,
    medicalHistory: "",
  });
  const [paymentInfo, setPaymentInfo] = useState({
    totalCost: null,
    advancePayment: null,
    remainingBalance: null,
    notes: "",
  });
  const [activeTabKey, setActiveTabKey] = useState("");

  const generateSessions = (count) => {
    return Array.from({ length: count }, (_, index) => ({
      key: `session-${Date.now()}-${index}`,
      dateTime: null,
      note: "",
      price: 0,
    }));
  };

  const addService = (serviceId, serviceName) => {
    const serviceExits = selectedServices?.find(
      (service) => service?.serviceId === serviceId,
    );
    if (!serviceExits) {
      const newService = {
        serviceId,
        serviceName,
        noOfSessions: 1,
        total: 0,
        advance: 0,
        remaining: 0,
        sessions: generateSessions(1),
      };
      setSelectedServices((prev) => [...prev, newService]);
      if (!activeTabKey) setActiveTabKey(serviceId);
    }
  };

  const removeService = (serviceId) => {
    const remainingServices = selectedServices.filter(
      (service) => service?.serviceId !== serviceId,
    );
    setSelectedServices([...remainingServices]);
    if (activeTabKey === serviceId)
      setActiveTabKey(
        remainingServices?.length ? remainingServices[0]?.serviceId : null,
      );
  };

  const updateSessionCount = (serviceId, count) => {
    setSelectedServices((prev) =>
      prev.map((service) => {
        if (service?.serviceId === serviceId) {
          const currentSessions = service?.sessions;
          let newSessions;

          if (count > currentSessions?.length) {
            const additionalSessions = generateSessions(
              count - currentSessions?.length,
            );
            newSessions = [...currentSessions, ...additionalSessions];
          } else newSessions = currentSessions.slice(0, count);
          return {
            ...service,
            noOfSessions: count,
            sessions: newSessions,
          };
        }
        return service;
      }),
    );
  };

  // const updateSessionData = (serviceId, sessionKey, field, value) => {
  //   setSelectedServices((prev) =>
  //     prev.map((service) => {
  //       if (service?.serviceId === serviceId) {
  //         let sessions = ["price", "note", "dateTime"]?.includes(field)
  //           ? service?.sessions?.map((session) => {
  //               if (session?.key === sessionKey) {
  //                 return { ...session, [field]: value };
  //               }
  //               return session;
  //             })
  //           : service?.sessions;

  //         let total =
  //           field === "price"
  //             ? sessions?.reduce(
  //                 (sum, session) => sum + (session?.price || 0),
  //                 0,
  //               )
  //             : service?.total;
  //         let advance = field === "advance" ? value : service?.advance;
  //         let remaining = total - advance;
  //         return {
  //           ...service,
  //           total,
  //           advance,
  //           remaining,
  //           sessions,
  //         };
  //       }
  //       return service;
  //     }),
  //   );
  // };

  // In your AppointmentContext — updateSessionData function
  const updateSessionData = (serviceId, sessionKey, field, value) => {
    setSelectedServices((prev) =>
      prev.map((service) => {
        if (service.serviceId !== serviceId) return service;
        if (sessionKey === null) {
          return { ...service, [field]: value };
        }
        const updatedSessions = service.sessions.map((session) =>
          session.key === sessionKey ? { ...session, [field]: value } : session,
        );

        const total = updatedSessions.reduce(
          (sum, s) => sum + (s.price || 0),
          0,
        );
        const remaining = Math.max(total - (service.advance || 0), 0);

        return {
          ...service,
          sessions: updatedSessions,
          total,
          remaining,
        };
      }),
    );
  };
  const updatePatientInfo = (field, value) => {
    setPatientInfo((prev) => ({ ...prev, [field]: value }));
  };

  const updatePaymentInfo = (field, value) => {
    setPaymentInfo((prev) => ({ ...prev, [field]: value }));
  };

  const calculateTotalPrice = () => {
    return selectedServices?.reduce((total, service) => {
      return (
        total +
        service?.sessions?.reduce(
          (sum, session) => sum + (session.price || 0),
          0,
        )
      );
    }, 0);
  };

  const setSelectedServicesDirectly = (services) => {
    setSelectedServices(services);
    if (services.length > 0) {
      setActiveTabKey(services[0].serviceId);
    }
  };

  const resetAppointment = () => {
    setServices([]);
    setSelectedServices([]);
    setPatientInfo({
      fullName: "",
      fatherName: "",
      contact: "",
      cnic: "",
      address: "",
      occupation: "",
      sourceInfo: "",
      gender: "",
      age: null,
      medicalHistory: "",
    });
    setPaymentInfo({
      totalCost: null,
      advancePayment: null,
      remainingBalance: null,
      notes: "",
    });
    setActiveTabKey("");
  };

  return (
    <AppointmentContext.Provider
      value={{
        selectedServices,
        services,
        patientInfo,
        paymentInfo,
        activeTabKey,
        addService,
        removeService,
        updateSessionCount,
        updateSessionData,
        updatePatientInfo,
        updatePaymentInfo,
        setActiveTabKey,
        calculateTotalPrice,
        resetAppointment,
        setSelectedServicesDirectly,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};
